import { Button as ShadCnButton } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import {
  getFileIconByStatusAndName,
  getStatusIconByStatus,
} from "@/utils/questionnaire/questionnaireStatus"
import { IconTrash, IconWindowMaximize } from "@tabler/icons-react"
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner"
import { Typography } from "../Typography/Typography"

interface FileInfoProps {
  fileTitle: string
  fileSize?: string
  isLoading?: boolean
  deleteButton?: boolean
  previewButton?: boolean
  selectedDocumentsCount?: number
  status?: QuestionnaireStatus
  onSelectDocuments?: () => void
  onDelete?: () => void
  disabled?: boolean
}

export const FileInfo: React.FC<FileInfoProps> = ({
  fileTitle,
  fileSize,
  isLoading = false,
  deleteButton = false,
  previewButton = false,
  selectedDocumentsCount = 0,
  disabled = false,
  status,
  onSelectDocuments,
  onDelete,
}) => {
  const { translate } = useTranslation()
  const StatusIcon = status && getStatusIconByStatus(status)
  const FileIcon = getFileIconByStatusAndName(status, fileTitle)

  return (
    <div
      className={classNames(
        "p-M",
        "flex flex-col",
        "gap-S",
        "rounded-lg border border-grey4",
      )}
    >
      <div className="flex justify-between">
        <FileIcon />
        {isLoading && <LoadingSpinner />}
        {!isLoading && StatusIcon && <StatusIcon className="h-10 w-10" />}
      </div>
      <div>
        <Typography as="h3" variant="textS" className="truncate">
          {fileTitle}
        </Typography>
        {fileSize && (
          <Typography as="p" variant="textS" className="truncate text-grey5">
            {`${translate("FILE_SIZE")} ${fileSize}`}
          </Typography>
        )}
        {selectedDocumentsCount > 0 ? (
          <div className="flex justify-start">
            <ShadCnButton
              variant="link"
              className="h-auto px-0 text-center"
              onClick={onSelectDocuments}
            >
              {translate("QUESTIONNAIRE_PREVIEW/DOCUMENTS_USED_BUTTON", {
                count: selectedDocumentsCount,
              })}
            </ShadCnButton>
          </div>
        ) : null}
      </div>

      {previewButton || deleteButton ? (
        <div className="flex justify-between">
          {previewButton && (
            <button className="flex" onClick={() => {}}>
              <IconWindowMaximize stroke="1" className="text-grey5" />
              <Typography
                className="self-center pl-1 text-grey5"
                as="p"
                variant={"textXS"}
              >
                {translate("FILE_INFO/PREVIEW")}
              </Typography>
            </button>
          )}
          {deleteButton && !disabled && (
            <button className="group flex" onClick={onDelete}>
              <IconTrash
                stroke="1"
                className="text-grey5 group-hover:text-white"
              />
              <Typography
                className="self-center pl-1 text-grey5 group-hover:text-white"
                as="p"
                variant={"textXS"}
              >
                {translate("FILE_INFO/DELETE")}
              </Typography>
            </button>
          )}
        </div>
      ) : null}
    </div>
  )
}
